/**
 * PurchasesContext — provides current plan state and IAP actions to the app.
 *
 * Wrap your app with <PurchasesProvider> inside <AuthProvider>.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  initRevenueCat,
  loginRevenueCat,
  getCurrentPlan,
  getOfferings,
  purchasePackage,
  restorePurchases,
  type RCOffering,
  type RCPackage,
  type PurchaseResult,
} from '../lib/purchasesService';
import { type PlanInfo, isNativePlatform } from '../lib/revenuecat';

interface PurchasesContextValue {
  /** Current resolved plan */
  plan: PlanInfo;
  /** True while loading plan from RC */
  planLoading: boolean;
  /** Current RC offerings (packages with prices) */
  offering: RCOffering | null;
  /** Re-fetch plan info from RC */
  refreshPlan: () => Promise<void>;
  /** Purchase a package */
  purchase: (pkg: RCPackage) => Promise<PurchaseResult>;
  /** Restore previous purchases */
  restore: () => Promise<PurchaseResult>;
  /** Whether IAP is available on this device */
  iapAvailable: boolean;
}

const PurchasesContext = createContext<PurchasesContextValue | null>(null);

export function PurchasesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [plan, setPlan] = useState<PlanInfo>({ tier: 'free', isActive: false });
  const [planLoading, setPlanLoading] = useState(true);
  const [offering, setOffering] = useState<RCOffering | null>(null);
  const iapAvailable = isNativePlatform();

  // Initialise RC once user is known
  useEffect(() => {
    let cancelled = false;

    async function init() {
      setPlanLoading(true);
      try {
        // Configure RC (with or without user)
        await initRevenueCat(user?.id);

        // If user exists, link their identity
        if (user?.id) {
          await loginRevenueCat(user.id);
        }

        // Fetch current plan + offerings in parallel
        const [currentPlan, currentOffering] = await Promise.all([
          getCurrentPlan(),
          getOfferings(),
        ]);

        if (!cancelled) {
          setPlan(currentPlan);
          setOffering(currentOffering);
        }
      } catch (err) {
        console.error('[PurchasesContext] init error:', err);
      } finally {
        if (!cancelled) setPlanLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [user?.id]);

  const refreshPlan = useCallback(async () => {
    try {
      const currentPlan = await getCurrentPlan();
      setPlan(currentPlan);
    } catch (err) {
      console.error('[PurchasesContext] refreshPlan error:', err);
    }
  }, []);

  const purchase = useCallback(async (pkg: RCPackage): Promise<PurchaseResult> => {
    const result = await purchasePackage(pkg);
    if (result.success && result.plan) {
      setPlan(result.plan);
    }
    return result;
  }, []);

  const restore = useCallback(async (): Promise<PurchaseResult> => {
    const result = await restorePurchases();
    if (result.success && result.plan) {
      setPlan(result.plan);
    }
    return result;
  }, []);

  return (
    <PurchasesContext.Provider value={{
      plan,
      planLoading,
      offering,
      refreshPlan,
      purchase,
      restore,
      iapAvailable,
    }}>
      {children}
    </PurchasesContext.Provider>
  );
}

export function usePurchases(): PurchasesContextValue {
  const ctx = useContext(PurchasesContext);
  if (!ctx) throw new Error('usePurchases must be used within PurchasesProvider');
  return ctx;
}

/** Convenience hook — just returns the current tier string */
export function usePlanTier() {
  const { plan, planLoading } = usePurchases();
  return { tier: plan.tier, planLoading };
}

/** Returns true if the user has an active paid plan */
export function useIsPro() {
  const { plan } = usePurchases();
  return plan.tier === 'pro' || plan.tier === 'enterprise';
}
